import { getValidContestStatuses } from "@/utils/contest-applications"
import { FieldsGroups } from "@/types/api/fields"
import type { Team } from "@/types/api/team"
import {
  requestStatesForDetails,
  requestStatesForTrip,
  requestStates,
} from "@/utils/requests"
import type { EventRequest } from "@/types/api/requests"
import { getAge, toDateString } from "@/utils/birthdate"
import { MFM_EVENT_ALIAS, getIsContestInviteValid } from "@/utils/mfm"
import { getLs, storageKeys } from "@/utils/storage"

export interface UserNavLink {
  title: string | Localized | null
  path: string
  icon?: string
  children?: UserNavLink[]
  hideChildren?: boolean
  allowChildren?: boolean
  marked?: boolean
}

export const cleanPath = (path: string) => path.replace(/^\/(en)\//, "/")

export function isPathBelongsToLink (link: UserNavLink, path: string): boolean {
  path = cleanPath(path)

  const isRootMatch = (
    link.allowChildren
      ? path.startsWith(link.path)
      : link.path === path
  )

  if (isRootMatch) return true

  const isChildMatch = (link.children || [])
    .some(child => child.path === path)

  return isChildMatch
}

export function getIsContestInvited (): boolean {
  return getIsContestInviteValid(getLs(storageKeys.contestInviteToken))
}

export function isContestAllowed (
  request: EventRequest,
  isInvited: boolean = getIsContestInvited(),
): boolean {
  if (
    !getValidContestStatuses(request.event?.alias)
      .includes(request.status?.alias || "")
  )
    return false

  if (
    request.event?.alias === MFM_EVENT_ALIAS &&
    !isInvited &&
    (
      !request.event?.contest?.finish ||
      Date.now() > request.event.contest.finish
    )
  )
    return false

  if (!request.event?.features?.includes("CONTEST"))
    return false

  return true
}

const PROGRAM_ENROLL_STATUSES = [
  "participant",
  "participantEscort",
]

const PROGRAM_VIEW_STATUSES = [
  "attache",
  "artist",
  "volunteer",
  "guest",
  "child",
  "moderator",
  "partner",
  "visitor",
  "honoredGuest",
  "media",
  "guestEscort",
  "speaker",
  "guide",
  "expert",
  "exhibitor",
]

const PROGRAM_STATES = [
  "confirmed",
  "confirmedPaidByDirectorate",
  "confirmedPaidByRegion",
  "confirmedWithoutCompensation",
  "confirmedWithoutServices",
  "participated",
]

export function isProgramAllowed (request: EventRequest): boolean {
  if (!request?.state) return false

  if (!request.event?.features?.includes("PROGRAM"))
    return false

  if (!PROGRAM_STATES.includes(request.state))
    return false

  const alias = request.status?.alias || ""

  return PROGRAM_ENROLL_STATUSES.includes(alias) ||
    PROGRAM_VIEW_STATUSES.includes(alias)
}

export function isProgramEnrollAllowed (request: EventRequest): boolean {
  if (!isProgramAllowed(request)) return false

  return PROGRAM_ENROLL_STATUSES.includes(request.status?.alias || "")
}

export function isEventDocsAllowed (request: EventRequest): boolean {
  if (!request || !request.state) return false

  if (!request.event?.features?.includes("DOCUMENT"))
    return false

  const allowedStates = [
    "confirmedPaidByDirectorate",
    "confirmedPaidByRegion",
    "confirmedWithoutCompensation",
    "confirmedWithoutServices",
    "participated",
  ]

  return Boolean(allowedStates.includes(request.state) &&
    request.recommendations?.approvedByDirection)
}

export function isTripAllowed (request: EventRequest): boolean {
  if (!request.event?.features?.includes("TRAVEL"))
    return false

  return Boolean(request &&
    request.state &&
    requestStatesForTrip.includes(request.state) &&
    request.ticket?.id)
}

export function isRegionalProgramAllowed (request: EventRequest): boolean {
  if (!request) return false

  if (!request.event?.features?.includes("REGIONAL_PROGRAM"))
    return false

  if (
    !request.attributes?.regprogramparticipate &&
    !request.attributes?.expeditionprogram
  ) return false

  if (!request.state) return false
  const states: (keyof typeof requestStates)[] = [
    "invitedPaidByDirectorate",
    "invitedWithoutCompensation",
    "confirmedPaidByDirectorate",
    "confirmedWithoutCompensation",
    "participated",
  ]
  if (!states.includes(request.state)) return false

  if (!request.birthdate) return false
  if (request.event.alias === MFM_EVENT_ALIAS) {
    const birtdate = toDateString(request.birthdate)
    if (birtdate <= "1990-09-20" || birtdate >= "2008-09-09") return false
  } else {
    const age = getAge(request.birthdate)
    if (age < 18 || age > 35) return false
  }

  return true
}

export function isArtMastersOpenAllowed (request: EventRequest): boolean {
  if (!request) return false

  if (request.event?.alias !== MFM_EVENT_ALIAS) return false

  return (request.recommendations?.artMastersInvitation || "INIT") !== "INIT"
}

export function isHabitationAllowed (request: EventRequest): boolean {
  if (!request.event?.features?.includes("TRAVEL"))
    return false

  return Boolean(request.roomReservation)
}

export function isFoodAllowed (request: EventRequest): boolean {
  if (!request.event?.features?.includes("TRAVEL"))
    return false

  return Boolean(request.foodStreams?.length)
}

export function isTeamAllowed (
  team: Team | null, request: EventRequest,
): boolean {
  if (!request.event?.features?.includes("TEAM"))
    return false

  return Boolean(team)
}

export function isFeedbackAllowed (request: EventRequest): boolean {
  if (!request.event?.features?.includes("FEEDBACK"))
    return false

  return request.state === "participated"
}

export function isPcrAllowed (request: EventRequest): boolean {
  if (!request.event?.features?.includes("PCR"))
    return false

  return [
    "confirmed",
    "confirmedPaidByDirectorate",
    "confirmedPaidByRegion",
    "confirmedWithoutCompensation",
    "confirmedWithoutTravel",
    "confirmedWithoutServices",
    "participated",
  ].includes(request.state || "")
}

export function getEventTabs (
  eventAlias: string,
  request: EventRequest | null,
  team: Team | null,
  hasProgramSessions: Nullable<boolean> = null,
): UserNavLink[] {
  const getPath = (path: string) => `/user/events/my/${eventAlias}/${path}`

  const tabs = []

  if (request?.state) {
    if (isContestAllowed(request))
      tabs.push({
        title: "contestSelection",
        path: getPath("contest"),
      })

    if (requestStatesForDetails.includes(request.state))
      tabs.push({
        title: "confirmationOfParticipation",
        path: getPath("participation"),
      })

    if (isProgramAllowed(request) && hasProgramSessions !== false)
      tabs.push({
        title: "program",
        path: getPath("program"),
      })

    if (isTripAllowed(request))
      tabs.push({
        title: "myTrip",
        path: getPath("trip"),
      })

    if (isRegionalProgramAllowed(request))
      tabs.push({
        title: "regionalProgram",
        path: getPath("regional-program"),
      })

    if (isArtMastersOpenAllowed(request))
      tabs.push({
        title: "artMastersOpen",
        path: getPath("art-masters-open"),
      })

    if (isHabitationAllowed(request))
      tabs.push({
        title: "habitation",
        path: getPath("accommodation"),
      })

    if (isFoodAllowed(request))
      tabs.push({
        title: "food",
        path: getPath("food"),
      })

    if (isTeamAllowed(team, request))
      tabs.push({
        title: "myTeam",
        path: getPath("team"),
      })

    if (isEventDocsAllowed(request))
      tabs.push({
        title: "myDocuments",
        path: getPath("documents"),
      })

    if (isFeedbackAllowed(request))
      tabs.push({
        title: "feedback",
        path: getPath("feedback"),
      })

    if (isPcrAllowed(request))
      tabs.push({
        title: "pcr",
        path: getPath("pcr"),
      })
  }

  return tabs
}

export function filterFormGroups (formSchema: FieldsGroups): FieldsGroups {
  return formSchema
    .filter((group) => {
      return group.alias &&
        group.title &&
        group.fields.length &&
        group.fields.every(f => !f.readOnly)
    })
}

export function formSchemaToLinks (
  formSchema: FieldsGroups,
  path: string,
  markedGroups: string[] = [],
): UserNavLink[] {
  return filterFormGroups(formSchema)
    .map((group) => {
      return {
        title: group.title,
        path: `${path}/${group.alias}`,
        marked: markedGroups.includes(group.alias),
      }
    })
}
